import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, Col, Dropdown, Collapse, Row, ListGroup, Breadcrumb, Card, Tab, Nav, Form, Button, Spinner } from 'react-bootstrap';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FileText, ExternalLink, MoreVertical, Check } from 'react-feather';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const PlansSettions = ({ activeKey }) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(null); // State for loading

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const auth = useAuthUser(); // Get the authenticated user data
	const [dataSubscriptions, setDataSubscriptions] = useState(null); // State

	// API-Call for Subscriptions
	const getSubscriptions = async () => {
		setIsLoading(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Plans
		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/subscriptions`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setDataSubscriptions({ data: responseData });
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	useEffect(() => {
		if (activeKey === 'plans') {
			getSubscriptions();
		}
	}, [activeKey]); // Fetch plans when the active key changes

	// Custom Toggle for Dropdown
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<a
			href=""
			ref={ref}
			onClick={e => {
				e.preventDefault();
				onClick(e);
			}}
			style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
		>
			{children}
		</a>
	));

	// ActionMenu Component
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.id, 'show')}>
						<ExternalLink size="15px" className="dropdown-item-icon" /> {t('Show')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.id, 'download')}>
						<FileText size="15px" className="dropdown-item-icon" /> {t('Download')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	// Handler for ActionMenu actions
	const handleRowClick = (id, action) => {
		if (action === 'show') {
			window.open(dataInvoices.data.find(inv => inv.id === id)?.hosted_invoice_url, '_blank', 'noopener,noreferrer');
		} else if (action === 'download') {
			window.open(dataInvoices.data.find(inv => inv.id === id)?.invoice_pdf, '_blank', 'noopener,noreferrer');
		}
	};

	// Helper to get current plan info
	const getCurrentSubscription = () => {
		if (!dataSubscriptions || !dataSubscriptions.data || dataSubscriptions.data.length === 0) return null;
		const subscription = dataSubscriptions.data[0];
		const plan = subscription.plan || (subscription.items?.data?.[0]?.plan);
		const price = subscription.items?.data?.[0]?.price;
		const product = subscription.product;
		const isTrial = subscription.status === 'trialing' && subscription.trial_end;
		return {
			id: plan.id,
			name: product?.name || plan?.id || t('Unknown Plan'),
			amount: (price?.unit_amount ?? plan?.amount ?? 0) / 100,
			currency: (price?.currency ?? plan?.currency ?? 'eur').toUpperCase(),
			interval: price?.recurring?.interval || plan?.interval || '',
			status: subscription.status,
			canceled: {
				cancel_at_period_end: subscription.cancel_at_period_end || false,
				cancel_at: subscription.cancel_at || null,
				canceled_at: subscription.canceled_at || null,
			},
			nextBilling: subscription.items?.data?.[0]?.current_period_end
				? new Date(subscription.items.data[0].current_period_end * 1000).toLocaleDateString()
				: null,
			trial: isTrial
				? {
					end: subscription.trial_end || null,
					start: subscription.trial_start || null
				}
				: null,
			marketing_features: product?.marketing_features || [],
		};
	};

	const currentSubscription = getCurrentSubscription();

	// Helper function to calculate remaining trial days
	const calculateDaysLeft = (endTimestamp) => {
		const now = new Date();
		const end = new Date(endTimestamp * 1000);
		const diffTime = end - now;
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row className="mb-4">
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="shadow-sm border-0">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Your current Plan')}</h4>
							{currentSubscription ? (
								<>
								<div className="mt-2">
									<span
										className={`badge bg-${currentSubscription.status === 'active' ? 'success' : currentSubscription.status === 'trialing' ? 'warning' : 'secondary'}${currentSubscription.status === 'trialing' ? ' text-dark' : ''}`}
									>
										{t(currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1))}

											{currentSubscription.trial?.end ? (
												(() => {
													const daysLeft = calculateDaysLeft(currentSubscription.trial.end);

													return daysLeft > 0 ? (
														<span className="ms-1 text-dark">
															<strong>{daysLeft}{' '}{t('days left')}</strong>
														</span>
													) : (
														<span className="ms-1 text-dark">

														</span>
													);
												})()
											) : null}
									</span>
									{currentSubscription.canceled?.cancel_at  ? (
										<span className="badge bg-warning ms-1">
										{(() => {
											const daysLeft = calculateDaysLeft(currentSubscription.canceled.cancel_at);

											return daysLeft > 0 ? (
												<>												
												<span className="ms-1 text-dark">
													{t('Cancelled')}
												</span>
												<span className="ms-1 text-dark">
													<strong>{daysLeft}{' '}{t('days left')}</strong>
												</span>
												</>

											) : (
												<span className="ms-1 text-dark">
													{t('Cancelled')}
												</span>
											);
										})()}
										</span>
									) : null}
								</div>
								</>
							) : (
								<span className="badge bg-secondary">{t('No active plan')}</span>
							)}
						</Card.Header>
						<Card.Body className="">
							<Row>
								{isLoading ? (
									<div className="d-flex justify-content-center">
										<Spinner animation="border" role="status">
											<span className="visually-hidden">Loading...</span>
										</Spinner>
									</div>
								) : (
									<>
										{currentSubscription ? (
											<>
												<div className="d-flex align-items-start mb-3 mb-md-0">
													<div className="me-4">
														<div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: 64, height: 64 }}>
															<span className="fs-3 fw-bold text-white">
																{currentSubscription ? currentSubscription.name.charAt(0).toUpperCase() : ''}
															</span>
														</div>
													</div>
													<div>
													<h3 className="mb-0 fw-bold">{currentSubscription.name}</h3>
													<div className="text-muted">
														{currentSubscription.amount.toLocaleString(undefined, { style: 'currency', currency: currentSubscription.currency })} / {t(currentSubscription.interval)}
													</div>
													{/* If Trial */}
													{currentSubscription.trial && (
														<div className="small mt-1">
															{t('Trial period')}:
															{/* Show days left in trial */}
															{(() => {
																const daysLeft = calculateDaysLeft(dataSubscriptions.data[0].trial_end);

																return daysLeft > 0 ? (
																	<span className="ms-1 text-info">
																		<strong>{daysLeft}{' '}{t('days left')}</strong>
																	</span>
																) : (
																	<span className="ms-1 text-warning">
																		{t('Trial expired')}
																	</span>
																);
															})()}
															<span className="ms-2">
																(
																	{currentSubscription.trial.start
																		? `${new Date(currentSubscription.trial.start * 1000).toLocaleDateString()} - `
																		: ''}
																	{currentSubscription.trial.end
																		? new Date(currentSubscription.trial.end * 1000).toLocaleDateString()
																		: ''}
																)
															</span>
														</div>
													)}
													{/* If Paid Plan */}
													{currentSubscription.nextBilling && !currentSubscription.trial && currentSubscription.amount > 0 && (
														<div className="small text-muted mt-1">
															{t('Next billing date')}: {currentSubscription.nextBilling}
															{/* Show days left until next billing */}
															{(() => {
																if (currentSubscription.nextBilling) {
																	const now = new Date();
																	const next = new Date(currentSubscription.nextBilling * 1000); // Convert to milliseconds
																	const diffTime = next - now;
																	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
																	return diffDays > 0 ? (
																		<span className="ms-2 text-primary">
																			({t('Days left')}: {diffDays})
																		</span>
																	) : null;
																}
																return null;
															})()}
														</div>
													)}
													<hr className="m-0 mt-2"/>
													{/* Features Collapse */}
													{currentSubscription.marketing_features && currentSubscription.marketing_features.length > 0 && (
														<FeatureCollapse features={currentSubscription.marketing_features} t={t} />
													)}
													</div>
												</div>
											</>
										) : (
												<span>{t('You have no active plan. Please choose a plan to use our services.')}</span>
										)}
									</>
								)}
							</Row>
							<Row>
								<Col lg={12} md={12}>
									<div className="d-flex justify-content-end">
										<Link
											className="px-4 ms-2 btn btn-primary rounded"
											state={{ currentPlan: currentSubscription }} // Produkt über state übergeben
											to="/marketing/pages/compare-plan"
										>
											{/* If Paid Plan */}
											{currentSubscription && currentSubscription.amount > 0
												? t('Compare Plans')
												: t('Upgrade Plan')}
										</Link>
									</div>
								</Col>
							</Row>
							
						</Card.Body>
						<Card.Footer className="d-flex justify-content-end">
							<small>
								{t('Not haeppy?')}{' '}{t('Choose another Plan')}{' '}{t('or')}{' '}
								<Link
									target="_blank"
									rel="noopener noreferrer"
									to="/marketing/help-center/support"
									className="text-decoration-none"
								>
									{t('Contact our Help Desk Team')} <i className="fe fe-arrow-up-right"></i>
								</Link>
							</small>
						</Card.Footer>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);

// Collapse component for features
function FeatureCollapse({ features, t }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="mt-2">
			<Button
				variant="link"
				className="p-0 mb-2 text-decoration-none text-primary"
				onClick={() => setOpen(!open)}
				aria-controls="feature-collapse"
				aria-expanded={open}
			>
				{open ? t('Hide features') : t('Show features')}
			</Button>
			<Collapse in={open}>
				<div id="feature-collapse">
					<ListGroup bsPrefix="list-unstyled ">
						{features.map((item, index) => (
							<ListGroup.Item
								key={index}
								className="mb-1"
								bsPrefix="list-item"
							>
								<span className="text-success me-2">
									<Check size="15px" />
								</span>
								<span>{item.name || item}</span>
							</ListGroup.Item>
						))}
					</ListGroup>
				</div>
			</Collapse>
		</div>
	);
}

};

export default PlansSettions;